import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  AutocompleteRenderInputParams,
  Checkbox,
  FormInputText,
  PhoneNumberInput,
  Select,
} from '@hopskipdrive/react-components';
import { colorPalette } from '@rideiq/shared/components/variables';

import {
  CheckboxCell,
  FormContainer,
  RequiredMark,
  SelectionBanner,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from './ImportCaregiverForm.styles';
import { CaregiverRowData, ImportCaregiverFormData, MAX_CAREGIVERS, RELATIONSHIP_OPTIONS } from './types';
import { importCaregiverFormSchema } from './validationSchema';

interface ImportCaregiverFormProps {
  initialCaregivers: RidersContactForm[];
  onSubmit: (caregivers: RidersContactForm[]) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

export interface ImportCaregiverFormRef {
  addManually: () => void;
}

export const ImportCaregiverForm = forwardRef<ImportCaregiverFormRef, ImportCaregiverFormProps>(
  ({ initialCaregivers, onSubmit, setIsSubmitting }, ref) => {
    const defaultCaregivers: CaregiverRowData[] = useMemo(
      () =>
        initialCaregivers.map((caregiver) => ({
          selected: caregiver.selected ?? false,
          isFromSis: caregiver.isFromSis || false,
          firstName: caregiver.firstName,
          lastName: caregiver.lastName,
          relationship: caregiver.relationship || '',
          phoneNumber: caregiver.phoneNumber || '',
          email: caregiver.email || '',
        })),
      [initialCaregivers]
    );

    const {
      control,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = useForm<ImportCaregiverFormData>({
      defaultValues: {
        caregivers: defaultCaregivers,
      },
      resolver: yupResolver(importCaregiverFormSchema),
    });

    const { fields, append } = useFieldArray({
      control,
      name: 'caregivers',
    });

    const watchCaregivers = watch('caregivers');
    const selectedCount = watchCaregivers?.filter((c) => c.selected).length || 0;
    const isAllSelected = fields.length > 0 && selectedCount === fields.length;
    const isIndeterminate = selectedCount > 0 && selectedCount < fields.length;
    const tooManySelected = selectedCount > MAX_CAREGIVERS;

    const [selectionError, setSelectionError] = useState<string | null>(null);

    useEffect(() => {
      if (selectedCount > 0) {
        setSelectionError(null);
      }
    }, [selectedCount]);

    const handleSelectAll = () => {
      const newSelected = !isAllSelected;

      fields.forEach((_, index) => {
        setValue(`caregivers.${index}.selected`, newSelected);
      });
    };

    useImperativeHandle(ref, () => ({
      addManually: () => {
        append({
          selected: false,
          isFromSis: false,
          firstName: '',
          lastName: '',
          relationship: '',
          phoneNumber: '',
          email: '',
        });
      },
    }));

    const handleFormSubmit = (data: ImportCaregiverFormData) => {
      if (selectedCount === 0) {
        setSelectionError('Please select up to 5 caregivers to import, or click Add manually.');

        return;
      }

      if (tooManySelected) {
        return;
      }

      setSelectionError(null);
      setIsSubmitting(true);

      const allCaregivers: RidersContactForm[] = data.caregivers.map((caregiver) => ({
        firstName: caregiver.firstName,
        lastName: caregiver.lastName,
        role: 'caregiver',
        relationship: caregiver.relationship,
        phoneNumber: caregiver.phoneNumber,
        email: caregiver.email,
        isFromSis: caregiver.isFromSis,
        selected: caregiver.selected,
      }));

      onSubmit(allCaregivers);
      setIsSubmitting(false);
    };

    return (
      <form id="import-caregiver-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <FormContainer>
          <Alert
            severity="info"
            title="We found caregivers who may be associated with this rider"
            message={`Select up to ${MAX_CAREGIVERS} caregivers to add to the rider's profile.`}
            sx={{
              backgroundColor: colorPalette.blue98,
              color: colorPalette.blue30,
              '& .MuiAlert-icon': { color: colorPalette.blue65 },
              '& .MuiAlertTitle-root': { color: colorPalette.blue30 },
            }}
          />

          {tooManySelected && (
            <Alert
              severity="error"
              message={`You may import a maximum of ${MAX_CAREGIVERS} caregivers. Please remove one or more selections.`}
              sx={{
                backgroundColor: colorPalette.red95,
                color: colorPalette.red20,
                '& .MuiAlert-icon': { color: colorPalette.red43 },
                '& .MuiAlert-message': { fontWeight: 700 },
              }}
            />
          )}

          {!tooManySelected && selectionError && (
            <Alert
              severity="error"
              message={selectionError}
              sx={{
                backgroundColor: colorPalette.red95,
                color: colorPalette.red20,
                '& .MuiAlert-icon': { color: colorPalette.red43 },
              }}
            />
          )}

          <TableContainer>
            {selectedCount > 0 && (
              <SelectionBanner>
                {selectedCount} {selectedCount === 1 ? 'caregiver is' : 'caregivers are'} selected.
              </SelectionBanner>
            )}

            <TableHeader>
              <CheckboxCell>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  data-testid="select-all-caregiver"
                />
              </CheckboxCell>
              <TableHeaderCell>First Name</TableHeaderCell>
              <TableHeaderCell>Last Name</TableHeaderCell>
              <TableHeaderCell>Relationship</TableHeaderCell>
              <TableHeaderCell>
                Phone Number <RequiredMark>*</RequiredMark>
              </TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
            </TableHeader>

            <TableBody>
              {fields.map((field, index) => {
                const rowErrors = errors.caregivers?.[index];
                const isRowSelected = watchCaregivers?.[index]?.selected ?? false;

                return (
                  <TableRow key={field.id} $selected={isRowSelected} data-testid={`caregiver-row-${index}`}>
                    {/* Checkbox */}
                    <CheckboxCell>
                      <Controller
                        name={`caregivers.${index}.selected`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />
                        )}
                      />
                    </CheckboxCell>

                    {/* First Name */}
                    <TableCell>
                      <Controller
                        name={`caregivers.${index}.firstName`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FormInputText
                            value={value}
                            onChange={onChange}
                            variant="outlined"
                            placeholder="First Name"
                            size="small"
                            fullWidth
                            error={!!rowErrors?.firstName}
                            helperText={rowErrors?.firstName?.message}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Last Name */}
                    <TableCell>
                      <Controller
                        name={`caregivers.${index}.lastName`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FormInputText
                            value={value}
                            onChange={onChange}
                            variant="outlined"
                            placeholder="Last Name"
                            size="small"
                            fullWidth
                            error={!!rowErrors?.lastName}
                            helperText={rowErrors?.lastName?.message}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Relationship */}
                    <TableCell>
                      <Controller
                        name={`caregivers.${index}.relationship`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            options={RELATIONSHIP_OPTIONS}
                            value={value}
                            setValue={onChange}
                            sx={{ width: '100%' }}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                              <FormInputText
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder="Select..."
                                fullWidth
                              />
                            )}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Phone Number */}
                    <TableCell>
                      <Controller
                        name={`caregivers.${index}.phoneNumber`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <PhoneNumberInput
                            data-testid="phone-number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            initialValue={value}
                            onValueChange={onChange}
                            error={!!rowErrors?.phoneNumber}
                            helperText={rowErrors?.phoneNumber?.message}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Email */}
                    <TableCell>
                      <Controller
                        name={`caregivers.${index}.email`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FormInputText
                            value={value}
                            onChange={onChange}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="email@example.com"
                            error={!!rowErrors?.email}
                            helperText={rowErrors?.email?.message}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </TableContainer>
        </FormContainer>
      </form>
    );
  }
);

ImportCaregiverForm.displayName = 'ImportCaregiverForm';
